/**
 * VIB-755: Konfig for sentraliserte realtime-subscriptions. Hver entry
 * beskriver hvilken tabell vi lytter på, channel-id, og hvordan vi
 * ekstraherer context fra event-payload for invalidateFor-helperen.
 *
 * Når en ny tabell skal få realtime-dekning:
 * 1. Bekreft at tabellen finnes i CACHE_REGISTRY (src/lib/cache-registry)
 * 2. Legg til en entry her med context-extractor
 * 3. Verifiser med to-browser-test
 */
import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import type { TableName } from "@/lib/cache-registry";

export interface RealtimeTableConfig {
  /** TableName som matcher CACHE_REGISTRY. */
  readonly table: TableName;
  /** Channel-id for Supabase (må være unik per tabell). */
  readonly channelId: string;
  /** Ekstrahér context fra event-payload som passes til invalidateFor. */
  readonly extractContext: (
    payload: RealtimePostgresChangesPayload<Record<string, unknown>>,
  ) => Record<string, string | undefined>;
}

/**
 * Hent en string-felt fra payload.new eller payload.old (delete-events
 * har kun `old`, insert kun `new`, update har begge).
 */
function pickRowField(
  payload: RealtimePostgresChangesPayload<Record<string, unknown>>,
  field: string,
): string | undefined {
  const newRow = payload.new as Record<string, unknown> | undefined;
  const oldRow = payload.old as Record<string, unknown> | undefined;
  const value = newRow?.[field] ?? oldRow?.[field];
  return typeof value === "string" ? value : undefined;
}

export const REALTIME_TABLES: readonly RealtimeTableConfig[] = [
  {
    table: "projects",
    channelId: "realtime:projects",
    extractContext: (payload) => ({
      projectId: pickRowField(payload, "id"),
    }),
  },
  {
    table: "budget_lines",
    channelId: "realtime:budget_lines",
    extractContext: (payload) => ({
      projectId: pickRowField(payload, "project_id"),
    }),
  },
  {
    table: "time_entries",
    channelId: "realtime:time_entries",
    extractContext: (payload) => ({
      // time_entries har ikke direkte project_id i raden — project-scoped
      // queries faller til bred prefix-invalidering. Akseptabelt fordi
      // cross-user time_entries-events er sjeldne.
      userId: pickRowField(payload, "user_id"),
    }),
  },
  {
    // Tidligere subscribet per-rom i use-chat-messages med filter
    // `room_id=eq.<uuid>`. Flere filtrerte subscriptions på samme tabell
    // viste seg ustabilt (events kom ikke gjennom mottaker-klienten).
    // Bred subscription på hele tabellen + CACHE_REGISTRY ↔ roomId fan-out
    // matcher mønsteret som notifications/projects/budget_lines bruker.
    table: "chat_messages",
    channelId: "realtime:chat_messages",
    extractContext: (payload) => ({
      roomId: pickRowField(payload, "room_id"),
    }),
  },
  {
    // Tidligere subscribet per-prosjekt med filter project_id=eq.<uuid>
    // i use-project-comments — samme silent-failure som chat_messages.
    // Sentralisert subscription invaliderer ["project-comments", projectId].
    table: "project_comments",
    channelId: "realtime:project_comments",
    extractContext: (payload) => ({
      projectId: pickRowField(payload, "project_id"),
    }),
  },
  {
    // Tidligere per-prosjekt med project_id=eq.<uuid>-filter i
    // useProjectDocuments. Flyttet hit for konsistens med øvrige realtime-
    // tabeller — opplastede dokumenter skal dukke opp live.
    table: "project_documents",
    channelId: "realtime:project_documents",
    extractContext: (payload) => ({
      projectId: pickRowField(payload, "project_id"),
    }),
  },
  {
    // Tidligere per-oppgave med assignment_id=eq.<uuid>-filter i
    // useTaskComments. Sentralisert subscription invaliderer
    // ["task-comments", assignmentId] og ["task-comment-counts"].
    table: "task_comments",
    channelId: "realtime:task_comments",
    extractContext: (payload) => ({
      assignmentId: pickRowField(payload, "assignment_id"),
    }),
  },
  {
    // useChatRooms hadde en parallell `chat-rooms-rooms-${userId}`-channel
    // som invaliderte chat-rooms-listen ved hvilken som helst endring.
    // Bruker `created_by` for å treffe romoppretterens cache. Øvrige medlemmers
    // caches dekkes via chat_members-eventene som genereres ved insert.
    table: "chat_rooms",
    channelId: "realtime:chat_rooms",
    extractContext: (payload) => ({
      userId: pickRowField(payload, "created_by"),
    }),
  },
  {
    // VIB-3025: reaksjoner dukker opp live hos de andre i kanalen. Raden har
    // ikke kanalnøkkelen, så alle message-reactions-spørringer invalideres.
    table: "message_reactions",
    channelId: "realtime:message_reactions",
    extractContext: () => ({}),
  },
  {
    // useChatRooms hadde en `chat-rooms-members-${userId}`-channel for
    // last_read_at-oppdateringer (lesebekreftelser). Sentralisert: hver
    // chat_members-event sender event for medlemmets user_id, så alle
    // berørte klient-caches invalideres.
    table: "chat_members",
    channelId: "realtime:chat_members",
    extractContext: (payload) => ({
      userId: pickRowField(payload, "user_id"),
    }),
  },
  {
    // CRM-2.1: prosesser flytter seg live i Pipe når en kollega endrer noe.
    table: "opportunities",
    channelId: "realtime:opportunities",
    extractContext: (payload) => ({
      opportunityId: pickRowField(payload, "id"),
      customerId: pickRowField(payload, "customer_id"),
    }),
  },
  {
    // CRM-2.2: køen oppdateres live når en kollega lukker noe på en delt kunde.
    table: "follow_ups",
    channelId: "realtime:follow_ups",
    extractContext: (payload) => ({
      ownerId: pickRowField(payload, "owner_id"),
      customerId: pickRowField(payload, "customer_id"),
    }),
  },
  {
    // CRM-3.0: kortstabelen i Oppdag. Raden har ingen userId, så listeneren
    // invaliderer ["signal-queue"] bredt (CACHE_REGISTRY customer_signals).
    table: "customer_signals",
    channelId: "realtime:customer_signals",
    extractContext: (payload) => ({
      customerId: pickRowField(payload, "customer_id"),
      signalId: pickRowField(payload, "id"),
    }),
  },
  {
    // CRM-3.0: «Ikke for meg» fra et annet vindu fjerner signalet fra min kø.
    // RLS gjør at hver bruker bare får events for egne rader.
    table: "signal_dismissals",
    channelId: "realtime:signal_dismissals",
    extractContext: (payload) => ({
      signalId: pickRowField(payload, "signal_id"),
    }),
  },
];
